import { useEffect, useState } from 'react';
import { InlineFieldError } from '@/components/ui/FormFeedback';
import { CurrencyAmountInput } from '@/components/ui/CurrencyAmountInput';
import { DEFAULT_TEAM_COUNTRY, teamCountryCurrency } from '@/constants/teamCountries';

export interface HourlyWageEditTarget {
  operatorId: string;
  name: string;
  hourlyWage?: number;
  hint?: string;
  /** Team registration country — drives currency symbol. */
  country?: string | null;
}

interface EditHourlyWageModalProps {
  target: HourlyWageEditTarget | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (hourlyWage: number) => void;
}

function formatHourlyWageLabel(value: number | undefined, symbol: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return '—';
  const amount = Number.isInteger(value) ? String(value) : value.toFixed(2);
  return `${symbol}${amount}`;
}

export default function EditHourlyWageModal({
  target,
  submitting,
  onClose,
  onConfirm,
}: EditHourlyWageModalProps) {
  const [hourlyWage, setHourlyWage] = useState('');
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!target) {
      setHourlyWage('');
      setError(undefined);
      return;
    }
    const initial =
      typeof target.hourlyWage === 'number' && target.hourlyWage > 0
        ? String(target.hourlyWage)
        : '';
    setHourlyWage(initial);
    setError(undefined);
  }, [target]);

  if (!target) return null;

  const currency = teamCountryCurrency(target.country ?? DEFAULT_TEAM_COUNTRY);

  const handleConfirm = () => {
    const parsed = Number(hourlyWage);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Please enter an hourly wage greater than 0.');
      return;
    }
    setError(undefined);
    onConfirm(Math.round(parsed * 100) / 100);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[60]"
        onClick={onClose}
      />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[60] bg-white rounded-lg border border-gray-200 max-w-md mx-auto">
        <div className="p-3 pb-3">
          <div className="text-gray-800 font-bold text-base mb-1">Set Hourly Wage</div>
          <div className="text-gray-500 text-xs">{target.name}</div>
          {target.hint ? (
            <p className="text-gray-400 text-[11px] mt-1 leading-relaxed">{target.hint}</p>
          ) : null}
        </div>

        <div className="px-4 pb-5 space-y-3">
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-xs">
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-500 font-medium">Current</span>
              <span className="text-gray-800 font-semibold">
                {formatHourlyWageLabel(target.hourlyWage, currency.symbol)}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-normal text-gray-600 mb-2">
              Hourly Wage ({currency.code}) <span className="text-red-500">*</span>
            </label>
            <CurrencyAmountInput
              value={hourlyWage}
              onChange={setHourlyWage}
              currencySymbol={currency.symbol}
              disabled={submitting}
              aria-label="Hourly wage"
              aria-invalid={Boolean(error)}
            />
            <InlineFieldError id="edit-hourly-wage-error" message={error} />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 h-9 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium cursor-pointer disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="flex-1 h-9 rounded-lg bg-green-600 text-white text-xs font-medium cursor-pointer disabled:opacity-60 flex items-center justify-center gap-1.5"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
